import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, ButtonGroup, CircularProgress, IconButton,
  InputAdornment, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Clear, InfoOutlined, Search, ViewList, GridOn,
} from '@mui/icons-material';
import {
  getBillRateMatrix, setBillRateCell, setPayRateCell,
} from '../api';
import { useTerms } from '../context/TerminologyContext';

const PAY_SCOPE_HIDDEN_TEXT = 'Pay rates are shown only for your direct reports';

// Dash + info icon for a pay amount hidden by scope (employee is not a direct report).
const PayHiddenMark = () => (
  <>
    <span>—</span>
    <Tooltip title={PAY_SCOPE_HIDDEN_TEXT}>
      <InfoOutlined fontSize="inherit" color="action" sx={{ ml: 0.25, verticalAlign: 'middle' }} />
    </Tooltip>
  </>
);

// Profile pay rate text shared by the Single Client and Matrix views.
function PayRateText({ emp, canViewPayRates }) {
  if (emp.pay_hidden === true) return <PayHiddenMark />;
  if (emp.hourly_rate == null) {
    return canViewPayRates ? <span>—</span> : (
      <Tooltip title="Hidden — needs the View pay rates permission">
        <span>—</span>
      </Tooltip>
    );
  }
  return <>{`$${parseFloat(emp.hourly_rate).toFixed(2)}/hr`}</>;
}

/**
 * Client Bill Rates — admin tool to set what each client is billed per hour
 * per employee. Two view modes:
 *   • Single Client (default) — pick a client from a dropdown and edit just
 *     that client's employee rates as a vertical list. Scales to any number
 *     of clients and employees without horizontal scroll.
 *   • Matrix — the original employees × clients grid for cross-client
 *     comparison. Filterable by both axes.
 *
 * Resolution order at entry creation: employee × client cell rate → client's
 * default bill rate → employee's pay rate. The client default lives on the
 * Client Detail → Billing & Terms tab and is read-only here.
 */
export default function BillRateMatrix() {
  const { t } = useTerms();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({ employees: [], companies: [], rates: {}, payRates: {}, can_view_pay_rates: true });
  // Per-cell draft values (unsaved edits). Key: `${kind}:${empId}:${coId}`, kind = 'bill' | 'pay'.
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const saveTimers = useRef({});
  // Monotonic per-cell request sequence. When a save completes we only apply
  // its result if the sequence still matches — otherwise a stale in-flight
  // response would clobber a newer value the user typed.
  const saveSeq = useRef({});

  // View mode + the currently selected client for single-client view.
  // Default to single because horizontal scroll on the matrix gets unwieldy
  // as the client list grows; matrix stays available for cross-client compare.
  const [viewMode, setViewMode] = useState('single');
  const [selectedClientId, setSelectedClientId] = useState(null);

  // Matrix-view filters (only used when viewMode === 'matrix')
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getBillRateMatrix();
      setData(res.data);
      setDrafts({});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load bill rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Default the single-client picker to the first company alphabetically once
  // data loads. Companies arrive pre-sorted from the API. Re-runs only when
  // the selected ID disappears (e.g. client deleted from the list).
  useEffect(() => {
    if (data.companies.length === 0) return;
    if (!selectedClientId || !data.companies.some(c => c.id === selectedClientId)) {
      setSelectedClientId(data.companies[0].id);
    }
  }, [data.companies, selectedClientId]);

  // ── Cell save plumbing (shared by both views) ─────────────────────

  const scheduleCellSave = (key, saveFn, delayMs = 500) => {
    if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(() => {
      saveTimers.current[key] = null;
      saveFn();
    }, delayMs);
  };

  const flushCellSave = (key, saveFn) => {
    if (saveTimers.current[key]) {
      clearTimeout(saveTimers.current[key]);
      saveTimers.current[key] = null;
    }
    saveFn();
  };

  const saveCell = async (kind, empId, coId, rawValue) => {
    const key = `${kind}:${empId}:${coId}`;
    const mapName = kind === 'pay' ? 'payRates' : 'rates';
    const seq = (saveSeq.current[key] || 0) + 1;
    saveSeq.current[key] = seq;
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const payload = rawValue === '' ? null : parseFloat(rawValue);
      if (payload !== null && (!Number.isFinite(payload) || payload < 0)) {
        setError(kind === 'pay' ? 'Pay rate must be a non-negative number' : 'Bill rate must be a non-negative number');
        return;
      }
      await (kind === 'pay' ? setPayRateCell : setBillRateCell)(empId, coId, payload);
      if (saveSeq.current[key] !== seq) return;
      setData(prev => {
        const next = { ...prev, [mapName]: { ...(prev[mapName] || {}) } };
        if (payload === null) {
          if (next[mapName][empId]) {
            const row = { ...next[mapName][empId] };
            delete row[coId];
            next[mapName][empId] = row;
          }
        } else {
          next[mapName][empId] = { ...(next[mapName][empId] || {}), [coId]: payload };
        }
        return next;
      });
      setDrafts(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setError('');
    } catch (err) {
      if (saveSeq.current[key] === seq) {
        setError(err.response?.data?.error || 'Failed to save rate');
      }
    } finally {
      if (saveSeq.current[key] === seq) {
        setSaving(prev => ({ ...prev, [key]: false }));
      }
    }
  };

  const cellDisplayValue = (kind, empId, coId) => {
    const key = `${kind}:${empId}:${coId}`;
    if (drafts[key] !== undefined) return drafts[key];
    const rate = (kind === 'pay' ? data.payRates : data.rates)?.[empId]?.[coId];
    return rate != null ? String(rate) : '';
  };

  const handleCellChange = (kind, empId, coId, value) => {
    const key = `${kind}:${empId}:${coId}`;
    setDrafts(prev => ({ ...prev, [key]: value }));
    scheduleCellSave(key, () => saveCell(kind, empId, coId, value));
  };

  const handleCellBlur = (kind, empId, coId) => {
    const key = `${kind}:${empId}:${coId}`;
    if (drafts[key] !== undefined) {
      flushCellSave(key, () => saveCell(kind, empId, coId, drafts[key]));
    }
  };

  const clearCell = (kind, empId, coId) => {
    const key = `${kind}:${empId}:${coId}`;
    setDrafts(prev => ({ ...prev, [key]: '' }));
    flushCellSave(key, () => saveCell(kind, empId, coId, ''));
  };

  // ── Resolved-rate helper ──────────────────────────────────────────
  // Returns what HourTrax will actually charge for a new entry created today.
  const resolvedRate = (emp, co) => {
    const override = data.rates[emp.id]?.[co.id];
    if (override != null) return { value: override, source: 'override' };
    if (co.default_bill_rate != null) return { value: Number(co.default_bill_rate), source: `${t('client.singular', { lower: true })} default` };
    if (emp.pay_hidden === true) return { value: null, source: 'pay rate (hidden)', hiddenReason: 'scope' };
    if (emp.hourly_rate != null) return { value: Number(emp.hourly_rate), source: 'pay rate' };
    if (data.can_view_pay_rates === false) return { value: null, source: 'pay rate (hidden)', hiddenReason: 'permission' };
    return { value: null, source: null };
  };

  // ── Matrix-view filters ───────────────────────────────────────────
  const filteredEmployees = useMemo(() => {
    if (!employeeFilter.trim()) return data.employees;
    const q = employeeFilter.trim().toLowerCase();
    return data.employees.filter(e =>
      `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase().includes(q)
    );
  }, [data.employees, employeeFilter]);

  const filteredCompanies = useMemo(() => {
    if (!clientFilter.trim()) return data.companies;
    const q = clientFilter.trim().toLowerCase();
    return data.companies.filter(c => (c.name || '').toLowerCase().includes(q));
  }, [data.companies, clientFilter]);

  // ── Render ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.employees.length === 0 || data.companies.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Add at least one employee and one client to configure bill rates.
      </Alert>
    );
  }

  const selectedClient = data.companies.find(c => c.id === selectedClientId);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">{`${t('client.singular')} Bill Rates`}</Typography>
        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Sets what each client is billed per hour. Resolution at entry creation:
              </Typography>
              <Typography variant="body2" component="div">
                1. Employee × client override below
                <br />2. Client's default bill rate (set on Client → Billing & Terms)
                <br />3. Employee's pay rate (last resort)
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Leave a cell blank to fall back to the next tier. Changes apply to new entries; historical entries keep their snapshotted bill rate.
              </Typography>
            </Box>
          }
        >
          <InfoOutlined fontSize="small" color="action" />
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        {/* View toggle: single-client (default) vs matrix. */}
        <ButtonGroup size="small" variant="outlined">
          <Button
            startIcon={<ViewList fontSize="small" />}
            variant={viewMode === 'single' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('single')}
          >
            Single Client
          </Button>
          <Button
            startIcon={<GridOn fontSize="small" />}
            variant={viewMode === 'matrix' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('matrix')}
          >
            Matrix
          </Button>
        </ButtonGroup>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}

      {viewMode === 'single' ? (
        <SingleClientView
          canViewPayRates={data.can_view_pay_rates !== false}
          companies={data.companies}
          employees={data.employees}
          selectedClient={selectedClient}
          onClientChange={setSelectedClientId}
          cellDisplayValue={cellDisplayValue}
          handleCellChange={handleCellChange}
          handleCellBlur={handleCellBlur}
          clearCell={clearCell}
          saving={saving}
          drafts={drafts}
          ratesIndex={data.rates}
          payRatesIndex={data.payRates || {}}
          resolvedRate={resolvedRate}
        />
      ) : (
        <MatrixView
          canViewPayRates={data.can_view_pay_rates !== false}
          companies={filteredCompanies}
          employees={filteredEmployees}
          allCompaniesCount={data.companies.length}
          allEmployeesCount={data.employees.length}
          employeeFilter={employeeFilter}
          setEmployeeFilter={setEmployeeFilter}
          clientFilter={clientFilter}
          setClientFilter={setClientFilter}
          cellDisplayValue={cellDisplayValue}
          handleCellChange={handleCellChange}
          handleCellBlur={handleCellBlur}
          clearCell={clearCell}
          saving={saving}
          drafts={drafts}
          ratesIndex={data.rates}
        />
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Single-client view
// Vertical list scoped to one client. No horizontal scroll. The "resolved
// rate" column shows what will actually be billed if you stop editing here.
// ─────────────────────────────────────────────────────────────────────
function SingleClientView({
  canViewPayRates,
  companies, employees, selectedClient, onClientChange,
  cellDisplayValue, handleCellChange, handleCellBlur, clearCell,
  saving, drafts, ratesIndex, payRatesIndex, resolvedRate,
}) {
  const { t } = useTerms();
  if (!selectedClient) return null;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label={t('client.singular')}
          value={selectedClient.id}
          onChange={(e) => onClientChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 280 }}
        >
          {companies.map(c => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          Default bill rate:&nbsp;
          {selectedClient.default_bill_rate != null
            ? <strong>${Number(selectedClient.default_bill_rate).toFixed(2)}/hr</strong>
            : <em>none — falls back to each employee's pay rate</em>}
          &nbsp;<Typography variant="caption" component="span" color="text.secondary">
            (edit on Client → Billing & Terms)
          </Typography>
        </Typography>
      </Stack>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Pay Rate</TableCell>
                {canViewPayRates && (
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
                      <span>Override Pay Rate</span>
                      <Tooltip title="What this employee is paid per hour for this client's work — the pay side, separate from the bill rate. Blank = the employee's pay rate from their profile. 0 on a salaried employee = covered by salary: new entries for this client are billed but not paid (Pay Basis: Salary-covered). Applies to new entries only.">
                        <InfoOutlined fontSize="inherit" color="action" />
                      </Tooltip>
                    </Stack>
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Override Bill Rate</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Resolved Bill Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map(emp => {
                const key = `bill:${emp.id}:${selectedClient.id}`;
                const payKey = `pay:${emp.id}:${selectedClient.id}`;
                const value = cellDisplayValue('bill', emp.id, selectedClient.id);
                const payValue = cellDisplayValue('pay', emp.id, selectedClient.id);
                const hasOverride = ratesIndex[emp.id]?.[selectedClient.id] != null || drafts[key];
                const hasPayOverride = payRatesIndex[emp.id]?.[selectedClient.id] != null || drafts[payKey];
                const resolved = resolvedRate(emp, selectedClient);
                return (
                  <TableRow key={emp.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {emp.first_name} {emp.last_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <PayRateText emp={emp} canViewPayRates={canViewPayRates} />
                    </TableCell>
                    {canViewPayRates && (
                      <TableCell align="right">
                        {emp.pay_hidden === true ? <PayHiddenMark /> : (
                        <Stack direction="row" alignItems="center" spacing={0.25} justifyContent="flex-end">
                          <TextField
                            size="small"
                            type="number"
                            value={payValue}
                            placeholder={emp.hourly_rate != null ? String(emp.hourly_rate) : '—'}
                            onChange={(e) => handleCellChange('pay', emp.id, selectedClient.id, e.target.value)}
                            onBlur={() => handleCellBlur('pay', emp.id, selectedClient.id)}
                            inputProps={{ step: '0.01', min: 0, style: { textAlign: 'right', padding: '4px 6px' } }}
                            InputProps={{ startAdornment: '$' }}
                            sx={{ width: 110 }}
                            disabled={saving[payKey]}
                          />
                          {hasPayOverride && (
                            <Tooltip title="Clear (use the employee's pay rate)">
                              <IconButton size="small" onClick={() => clearCell('pay', emp.id, selectedClient.id)}>
                                <Clear fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Stack direction="row" alignItems="center" spacing={0.25} justifyContent="flex-end">
                        <TextField
                          size="small"
                          type="number"
                          value={value}
                          placeholder={selectedClient.default_bill_rate != null ? String(selectedClient.default_bill_rate) : '—'}
                          onChange={(e) => handleCellChange('bill', emp.id, selectedClient.id, e.target.value)}
                          onBlur={() => handleCellBlur('bill', emp.id, selectedClient.id)}
                          inputProps={{ step: '0.01', min: 0, style: { textAlign: 'right', padding: '4px 6px' } }}
                          InputProps={{ startAdornment: '$' }}
                          sx={{ width: 110 }}
                          disabled={saving[key]}
                        />
                        {hasOverride && (
                          <Tooltip title={`Clear (fall back to ${t('client.singular', { lower: true })} default)`}>
                            <IconButton size="small" onClick={() => clearCell('bill', emp.id, selectedClient.id)}>
                              <Clear fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {resolved.value != null ? (
                        <>
                          <Typography variant="body2" component="span">
                            ${Number(resolved.value).toFixed(2)}/hr
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({resolved.source})
                          </Typography>
                        </>
                      ) : resolved.source === 'pay rate (hidden)' ? (
                        <Tooltip title={resolved.hiddenReason === 'scope' ? PAY_SCOPE_HIDDEN_TEXT : "Falls back to the employee's pay rate, which needs the View pay rates permission to display."}>
                          <em style={{ opacity: 0.5 }}>—</em>
                        </Tooltip>
                      ) : <em style={{ opacity: 0.5 }}>—</em>}
                    </TableCell>
                  </TableRow>
                );
              })}
              {employees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canViewPayRates ? 5 : 4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No employees yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Empty Override = use the client's default bill rate. Changes save automatically. The Resolved Bill Rate column shows what will be charged on new entries.
      </Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Matrix view
// Cross-client compare for power users. Two filter boxes narrow rows and
// columns independently. Read-only client default shown under each column
// header so you know what an empty cell falls back to.
// ─────────────────────────────────────────────────────────────────────
function MatrixView({
  canViewPayRates, companies, employees, allCompaniesCount, allEmployeesCount,
  employeeFilter, setEmployeeFilter, clientFilter, setClientFilter,
  cellDisplayValue, handleCellChange, handleCellBlur, clearCell,
  saving, drafts, ratesIndex,
}) {
  const { t } = useTerms();
  const rateCellSx = { p: 0.5, minWidth: 110 };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder="Filter employees…"
          value={employeeFilter}
          onChange={(e) => setEmployeeFilter(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            endAdornment: employeeFilter ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setEmployeeFilter('')}><Clear fontSize="small" /></IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ width: { xs: '100%', sm: 240 } }}
        />
        <TextField
          size="small"
          placeholder={`Filter ${t('client.plural', { lower: true })}…`}
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            endAdornment: clientFilter ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setClientFilter('')}><Clear fontSize="small" /></IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ width: { xs: '100%', sm: 240 } }}
        />
        {(employeeFilter || clientFilter) && (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Showing {employees.length}/{allEmployeesCount} employees × {companies.length}/{allCompaniesCount} clients
          </Typography>
        )}
      </Stack>

      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 3, minWidth: 200 }}>
                  Employee / Pay Rate
                </TableCell>
                {companies.map(co => (
                  <TableCell key={co.id} align="center" sx={{ minWidth: 140 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{co.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {co.default_bill_rate != null
                        ? `default $${Number(co.default_bill_rate).toFixed(2)}/hr`
                        : 'no default'}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map(emp => (
                <TableRow key={emp.id} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    <Typography variant="body2">
                      {emp.first_name} {emp.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pay: <PayRateText emp={emp} canViewPayRates={canViewPayRates} />
                    </Typography>
                  </TableCell>
                  {companies.map(co => {
                    const key = `bill:${emp.id}:${co.id}`;
                    const value = cellDisplayValue('bill', emp.id, co.id);
                    const hasOverride = ratesIndex[emp.id]?.[co.id] != null || drafts[key];
                    return (
                      <TableCell key={co.id} align="center" sx={rateCellSx}>
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                          <TextField
                            size="small"
                            type="number"
                            value={value}
                            placeholder={co.default_bill_rate != null ? String(co.default_bill_rate) : '—'}
                            onChange={(e) => handleCellChange('bill', emp.id, co.id, e.target.value)}
                            onBlur={() => handleCellBlur('bill', emp.id, co.id)}
                            inputProps={{ step: '0.01', min: 0, style: { textAlign: 'right', padding: '4px 6px' } }}
                            InputProps={{ startAdornment: '$' }}
                            sx={{ width: 100 }}
                            disabled={saving[key]}
                          />
                          {hasOverride && (
                            <Tooltip title={`Clear (fall back to ${t('client.singular', { lower: true })} default)`}>
                              <IconButton size="small" onClick={() => clearCell('bill', emp.id, co.id)}>
                                <Clear fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {(employees.length === 0 || companies.length === 0) && (
                <TableRow>
                  <TableCell colSpan={companies.length + 1} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No matching {employees.length === 0 ? 'employees' : t('client.plural', { lower: true })}.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Empty cell = use the client's default bill rate. To set or change a client default, open the client and edit on the Billing & Terms tab. Changes save automatically.
      </Typography>
    </Box>
  );
}
